import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

type AnimationActivityContextType = {
  /** True while at least one registered table animation is running. */
  isAnimating: boolean;
  /**
   * Mark an animation as active. Returns an idempotent function that clears it
   * (safe to call from both a GSAP `onComplete` and an effect cleanup).
   */
  begin: () => () => void;
};

export const AnimationActivityContext = createContext<AnimationActivityContextType>({
  isAnimating: false,
  begin: () => () => {}
});

export const useAnimationActivity = () => useContext(AnimationActivityContext);

/**
 * Tracks how many transient table animations (chip flights, etc.) are running so
 * other features can hold off until the table is visually idle. Components call
 * `begin()` when their animation starts and invoke the returned function when it
 * finishes (or unmounts). `isAnimating` is true whenever the count is non-zero.
 */
export const AnimationActivityProvider = ({ children }: { children: React.ReactNode }) => {
  const countRef = useRef(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const begin = useCallback(() => {
    countRef.current += 1;
    setIsAnimating(true);

    let cleared = false;
    return () => {
      if (cleared) return;
      cleared = true;
      countRef.current = Math.max(0, countRef.current - 1);
      if (countRef.current === 0) setIsAnimating(false);
    };
  }, []);

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes, not on every provider re-render. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(() => ({ isAnimating, begin }), [isAnimating, begin]);

  return (
    <AnimationActivityContext.Provider value={value}>{children}</AnimationActivityContext.Provider>
  );
};
